import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scan, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api/client";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("admin@aetherscan.app");
  const [password, setPassword] = useState("admin123");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, fullName || "Reviewer");
      toast.success("Authentification réussie");
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec");
    } finally {
      setLoading(false);
    }
  };

  const demo = async () => {
    setLoading(true);
    try {
      await login("admin@aetherscan.app", "admin123");
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="absolute inset-0 tech-grid opacity-60" />
      <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-md animate-in-up">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/15 border border-primary/30">
            <Scan className="h-7 w-7 text-primary" />
          </div>
          <h1 className="font-mono text-3xl font-extrabold tracking-tight">AetherScan</h1>
          <p className="mt-1 text-sm text-muted-foreground">Revue & validation de scans de logement — RGB + IMU + ARCore</p>
        </div>

        <div className="glass rounded-xl border border-border/60 p-6 shadow-2xl">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-secondary p-1">
            {(["login", "register"] as const).map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => setMode(m)}
                className={`rounded-md py-2 text-sm font-medium transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {m === "login" ? "Connexion" : "Inscription"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="uppercase-label mb-1.5 block text-muted-foreground">Nom complet</label>
                <input
                  data-testid="register-name-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full rounded-md border border-input bg-background/60 px-3 py-2.5 text-sm outline-none focus:border-primary"
                  placeholder="Jean Dupont"
                />
              </div>
            )}
            <div>
              <label className="uppercase-label mb-1.5 block text-muted-foreground">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-input bg-background/60 px-3 py-2.5 text-sm outline-none focus:border-primary"
                placeholder="user@example.com"
                required
              />
            </div>
            <div>
              <label className="uppercase-label mb-1.5 block text-muted-foreground">Mot de passe</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background/60 px-3 py-2.5 text-sm outline-none focus:border-primary"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {mode === "login" ? "Se connecter" : "Créer le compte"}
            </button>
          </form>

          <button
            data-testid="demo-login-button"
            onClick={demo}
            disabled={loading}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-border py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground hover:border-primary/40"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-400" /> Accès démo rapide
          </button>
        </div>
        <p className="mt-4 text-center uppercase-label text-muted-foreground">Backend = source de vérité · Exports bloqués si conflits critiques</p>
      </div>
    </div>
  );
}
